import React from 'react';
import { X, Trash2, MoveVertical, Copy, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface BulkActionsBarProps {
  selectedCount: number;
  itemType: 'timelines' | 'chapters' | 'cards';
  onClearSelection: () => void;
  onDelete?: () => void;
  onReorder?: () => void;
  onDuplicate?: () => void;
  className?: string;
}

export const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedCount,
  itemType,
  onClearSelection,
  onDelete,
  onReorder,
  onDuplicate,
  className
}) => {
  if (selectedCount === 0) return null;

  return (
    <div className={cn(
      "fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-background/95 backdrop-blur-sm border rounded-full shadow-lg px-3 py-1.5 flex items-center gap-1.5",
      className
    )}>
      <Badge variant="secondary" className="gap-1.5 px-2 py-1 h-7">
        <CheckSquare className="h-3.5 w-3.5" />
        <span>{selectedCount}</span>
      </Badge>

      <TooltipProvider>
        {onReorder && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onReorder}
                className="h-8 w-8 p-0 rounded-full"
              >
                <MoveVertical className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reorder {itemType}</TooltipContent>
          </Tooltip>
        )}

        {onDuplicate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDuplicate}
                className="h-8 w-8 p-0 rounded-full"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Duplicate {itemType}</TooltipContent>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="h-8 w-8 p-0 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete {itemType}</TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              className="h-8 w-8 p-0 rounded-full ml-1"
            >
              <X className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear selection</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
